import ResolveWorker from "@workers/resolve-worker"
import UploadWorkerUrl from "@workers/upload-worker?worker&url"
import axios from "~axios"

/**
 * Base class for file upload tasks with Web Worker support
 *
 * Error Logging Architecture:
 * - Web Workers run in separate thread → their console logs don't reach Sentry
 * - Worker errors are passed to main thread via postMessage with errorDetails
 * - Main thread logs errors using common methods (logErrorToConsole, logErrorDetails)
 * - Sentry CaptureConsole integration captures console.error from main thread only
 *
 * Common error handling methods (for inheritance):
 * - logErrorToConsole() - simple string for Sentry
 * - logErrorDetails() - string + object for Sentry + browser debugging
 * - buildFlatErrorBase() - flat structure to avoid "[Object]" in Sentry
 * - createErrorWithContext() - Error object with stack trace for Sentry
 */
export class FileTask {
    file = null
    result = null
    progress = 0
    fileName = ""
    worker = null
    status = "progress"
    errorMessage = ""

    // ========== LIFECYCLE ==========

    constructor(file, createWorker = true) {
        this.file = file
        this.fileName = file.name

        if (createWorker) {
            const worker = ResolveWorker(UploadWorkerUrl)()

            worker.onmessage = event => this.onMessage(event)
            worker.onerror = event => this.onFailed(event)

            worker.postMessage({
                type: "start",
                file,
                meta: file?.meta
            })

            this.worker = worker
        }
    }

    // ========== PUBLIC API ==========

    abort() {
        this.worker.postMessage({
            type: "abort"
        })

        if (this.onError) {
            this.status = "aborted"
            this.onError("aborted")
        }
    }

    // ========== CALLBACKS ==========

    onProgress(data) {
        this.status = "progress"
        this.progress = parseInt(data?.progress * 100)
    }

    onSuccess(data) {
        this.result = JSON.parse(data?.response)?.data

        this.status = "checking"
        this.checkStatus()

        this.progress = 100
    }

    onFailed(data) {
        this.status = "failed"
        this.result = data?.response ? JSON.parse(data.response) : null
        this.message = this.result?.message || data?.message
        this.progress = 0

        const flatError = this.buildFlatErrorBase("File_Upload_Failed", this.message, {
            httpStatus: data?.errorDetails?.status,
            httpStatusText: data?.errorDetails?.statusText,
            xhrReadyState: data?.errorDetails?.readyState,
            xhrResponseURL: data?.errorDetails?.responseURL,
            resultMessage: this.result?.message
        })

        this.logErrorDetails("File Upload Failed", this.message, data?.errorDetails?.status, flatError)

        if (this.onError) {
            const error = this.createErrorWithContext(this.message || "File upload failed", data, flatError)
            this.onError(error)
        }
    }

    onMessage(event) {
        let data = event.data
        let { type } = data

        if (type === "progress") {
            this.onProgress(data)
        }

        if (type === "failed") {
            this.onFailed(data)
        }

        if (type === "success") {
            this.onSuccess(data)
        }
    }

    // ========== UTILITIES ==========

    checkStatus() {
        let checkStatus = setInterval(() => {
            const { uuid, url } = this.result
            if (!url) {
                axios.get(`/files/${uuid}`).then(response => {
                    let data = response.data.data
                    if (data.process_status === "completed" && !data.url.includes("fileservice")) {
                        this.result = {
                            ...this.result,
                            url: data.url,
                            uploaded_at: Date.now(),
                            ...data
                        }

                        if (this.onComplete) {
                            this.status = "completed"
                            this.onComplete(this.result)
                        }

                        clearInterval(checkStatus)
                    }
                })
            } else {
                if (this.onComplete) {
                    this.status = "completed"
                    this.onComplete(this.result)
                }
            }
        }, 2000)
    }

    // ========== COMMON ERROR HANDLING (for inheritance) ==========

    /**
     * Log error as a simple string to console
     * Simple strings are better captured by Sentry's CaptureConsole integration
     */
    logErrorToConsole(errorType, message, status) {
        console.error(`${errorType}: ${message} | File: ${this.fileName} | Status: ${status || "n/a"}`)
    }

    /**
     * Log both simple string (for Sentry) and detailed object (for browser debugging)
     * Two logs ensure: 1) Sentry gets parseable string, 2) Devs see full context
     */
    logErrorDetails(errorType, message, status, flatError) {
        this.logErrorToConsole(errorType, message, status)
        console.error(`${errorType} Details:`, flatError)
    }

    /**
     * Build flat error object for Sentry
     * Flat structure (no nested objects) ensures Sentry displays all fields
     * Nested objects often appear as "[Object]" in Sentry logs
     */
    buildFlatErrorBase(errorType, message, additionalData = {}) {
        return {
            errorType,
            errorMessage: message,
            fileName: this.fileName,
            timestamp: new Date().toISOString(),
            ...additionalData
        }
    }

    /**
     * Create Error object with context for Sentry exception tracking
     * Error objects provide stack traces, context is added as custom properties
     */
    createErrorWithContext(message, originalData, context) {
        const error = new Error(message || "Upload failed")
        error.originalData = originalData
        error.uploadContext = context
        return error
    }
}
